import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabaseClient';

/**
 * Hook to check and manage block status between two users
 * @param {string} currentUserId - The current user's ID
 * @param {string} targetUserId - The target user's ID to check block status with
 * @returns {Object} Block status and actions
 */
export function useBlockStatus(currentUserId, targetUserId) {
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false); // Current user blocked target
  const [blockedBy, setBlockedBy] = useState(false); // Target blocked current user
  const [error, setError] = useState(null);

  const fetchBlockStatus = useCallback(async () => {
    if (!currentUserId || !targetUserId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if current user has blocked target
      const { data: blockedByMe, error: err1 } = await supabase
        .from('blocked_users')
        .select('id')
        .eq('user_id', currentUserId)
        .eq('blocked_user_id', targetUserId)
        .maybeSingle();

      if (err1) throw err1;

      // Check if target has blocked current user
      const { data: blockedByThem, error: err2 } = await supabase
        .from('blocked_users')
        .select('id')
        .eq('user_id', targetUserId)
        .eq('blocked_user_id', currentUserId)
        .maybeSingle();

      if (err2) throw err2;

      setBlocked(!!blockedByMe);
      setBlockedBy(!!blockedByThem);
    } catch (err) {
      console.error('[useBlockStatus] Error fetching block status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, targetUserId]);

  useEffect(() => {
    fetchBlockStatus();
  }, [fetchBlockStatus]);

  const blockUser = useCallback(async () => {
    if (!currentUserId || !targetUserId) return { success: false, error: 'Missing user IDs' };

    try {
      const { error } = await supabase
        .from('blocked_users')
        .insert({ user_id: currentUserId, blocked_user_id: targetUserId });

      if (error) throw error;

      setBlocked(true);
      return { success: true };
    } catch (err) {
      console.error('[useBlockStatus] Error blocking user:', err);
      return { success: false, error: err.message };
    }
  }, [currentUserId, targetUserId]);

  const unblockUser = useCallback(async () => {
    if (!currentUserId || !targetUserId) return { success: false, error: 'Missing user IDs' };

    try {
      const { error } = await supabase
        .from('blocked_users')
        .delete()
        .eq('user_id', currentUserId)
        .eq('blocked_user_id', targetUserId);

      if (error) throw error;

      setBlocked(false);
      return { success: true };
    } catch (err) {
      console.error('[useBlockStatus] Error unblocking user:', err);
      return { success: false, error: err.message };
    }
  }, [currentUserId, targetUserId]);

  const toggleBlock = useCallback(async () => {
    if (blocked) {
      return unblockUser();
    } else {
      return blockUser();
    }
  }, [blocked, blockUser, unblockUser]);

  // Derived: is messaging blocked (either direction)?
  const isMessagingBlocked = blocked || blockedBy;

  // Get a human-readable message about the block status
  const getBlockMessage = useCallback(() => {
    if (blocked && blockedBy) {
      return 'You have blocked each other.';
    }
    if (blocked) {
      return 'You have blocked this user.';
    }
    if (blockedBy) {
      return 'This user has restricted messages from you.';
    }
    return null;
  }, [blocked, blockedBy]);

  return {
    loading,
    error,
    blocked,
    blockedBy,
    isMessagingBlocked,
    blockUser,
    unblockUser,
    toggleBlock,
    getBlockMessage,
    refetch: fetchBlockStatus,
  };
}

export default useBlockStatus;
